import React, { useEffect, useState } from 'react';

import useLeaderboard from '../hooks/useLeaderboard';
import DetailSection from './components/DetailSection';
import useTraderOffChainPoints from '../hooks/useTraderOffChainPoints';
import { useEssential } from './App';

const SRank: React.FC = props => {
  const { accountData } = useEssential();
  const [invalid, setInvalid] = useState('');

  useEffect(() => {
    if (!accountData.address) {
      setInvalid('N/A');
      return;
    }

    setInvalid('');
  }, [accountData.address]);

  const leaderboard = useLeaderboard();
  const traderPoints = useTraderOffChainPoints(accountData.address);

  const findAddressIndex = (address: string): number => {
    return leaderboard.findIndex(trader => trader.address === address);
  };

  const getCurrentRank = () => {
    if (invalid) {
      return invalid;
    }
    return (findAddressIndex(accountData.address) + 1).toString();
  };

  const getTraderPoints = () => {
    if (invalid) {
      return invalid;
    }
    return traderPoints ? traderPoints.toString() : '0';
  };

  const getRankColor = (rank: number): string => {
    switch (rank) {
      case 1:
        return '#0eca82'; // Gold color for top 1
      case 2:
        return '#ffd700'; // Silver color for top 2
      case 3:
        return '#cd7f32'; // Bronze color for top 3
      default:
        return ''; // No special color for other ranks
    }
  };

  const lastUpdatedAt = () => {
    // Get the current date
    const currentDate = new Date();

    // Get the last updated time (which is the start of the current day)
    const lastUpdated = new Date(currentDate.setHours(0, 0, 0, 0));

    // Format the date nicely
    const formattedDate = lastUpdated.toLocaleString();

    return (
      <div className="last-updated">
        <p>Last updated on: {formattedDate}</p>
      </div>
    );
  };

  return (
    <div className={`section-container py-5 sm:py-10 sm:px-14 px-5 relative`}>
      <div className="text-3xl !mb-5">"S" Rank</div>
      <div className="sm:w-[50%] !mb-10">
        Welcome to the S Points Leaderboard, where we celebrate active traders
        and benevolent liquidity providers in the Sudo ecosystem. This
        leaderboard ranks Sudo users based on cumulative points, earned through
        trading and providing liquidity. It allows you to monitor your total S
        Points, compare your points with other Sudo users and see your ranking
        within the community. Compete for the top spot, learn from the
        strategies of elite traders, and make your mark on the Sudo trading
        scene. Are you ready to join the ranks of Sudo's top elites?
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 !mb-10">
        <div
          className={
            'bg-primary/50 border-[1px] border-secondary rounded-lg !p-4 flex items-center justify-between'
          }
        >
          <div className="app-mint-overview-card-title flex row justify-start align-center">
            <div>Sudo Ranking</div>
          </div>
          <DetailSection
            title="Your Ranking"
            content={getCurrentRank()}
            active={false}
          />
          <DetailSection
            title="Your SPs"
            content={getTraderPoints()}
            active={false}
          />
        </div>
      </div>
      <div className="bg-primary/50 border-[1px] border-secondary rounded-lg">
        <div className="flex justify-between items-center !p-6 !pb-4">
          <div className="text-xl font-bold">Leaderboard</div>
          <div className="text">{lastUpdatedAt()}</div>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-200px)] sm:max-h-[500px] !px-6 !pb-10 relative custom-scrollbar">
          <table className="border-collapse table-auto w-[700px] sm:w-full text-base">
            <thead className="sticky top-0  bg-[rgb(9,12,16)] ">
              <tr>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left">
                  RANK
                </th>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left">
                  ADDRESS
                </th>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left">
                  POINTS
                </th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((trader, index) => {
                const rankColor = getRankColor(index + 1);
                const displayName =
                  trader.suiNsName ? trader.suiNsName : trader.address;
                return (
                  <tr key={index}>
                    <td
                      className="border-b border-secondary !py-5"
                      style={{ color: rankColor }}
                    >
                      {index + 1}
                    </td>
                    <td
                      className="border-b border-secondary !py-5"
                      style={{ color: rankColor }}
                    >
                      <a
                        href={`https://suivision.xyz/account/${trader.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View on Explorer"
                      >
                        {displayName}
                      </a>
                    </td>
                    <td
                      className="border-b border-secondary !py-5"
                      style={{ color: rankColor }}
                    >
                      {trader.points}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SRank;
